import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { createContext, memo, useMemo } from "react";
import type { DraftCollectionItem } from "../db/collections";
import { useGlobalConversationCollections } from "./global-conversation-collections-context";

export interface ConversationDraftsContextValue {
  drafts: DraftCollectionItem[];
  isLoading: boolean;
}

export const ConversationDraftsContext = createContext<
  ConversationDraftsContextValue | undefined
>(undefined);
ConversationDraftsContext.displayName = "ConversationDraftsContext";

export function useConversationDrafts(): ConversationDraftsContextValue {
  return useRequiredContext(ConversationDraftsContext);
}

interface ConversationDraftsProviderProps {
  conversationId: ConversationId;
  hasDraft: boolean;
  children: React.ReactNode;
}

/**
 * Provider that fetches drafts for a conversation once at the conversation level.
 * Uses the `hasDraft` field from the OpenSearch index to skip fetching when
 * there are no drafts, avoiding unnecessary API calls.
 */
export const ConversationDraftsProvider = memo(
  function ConversationDraftsProvider({
    conversationId,
    hasDraft,
    children,
  }: ConversationDraftsProviderProps) {
    const collections = useGlobalConversationCollections();

    // Only query drafts if the conversation has drafts according to OpenSearch
    // Return null to skip query when hasDraft is false
    const result = useLiveQuery(
      (q) =>
        hasDraft
          ? q
              .from({ draft: collections.drafts })
              .where(({ draft }) =>
                eq(draft.conversationId, String(conversationId)),
              )
          : null,
      [conversationId, hasDraft],
    );

    const value = useMemo(
      (): ConversationDraftsContextValue => ({
        drafts: hasDraft && result.data ? result.data : [],
        isLoading: hasDraft ? result.isLoading : false,
      }),
      [hasDraft, result.data, result.isLoading],
    );

    return (
      <ConversationDraftsContext.Provider value={value}>
        {children}
      </ConversationDraftsContext.Provider>
    );
  },
);
